var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { Preview, swivel as startSwivel } from "@rendering/vortex-core";
import { loadProduct } from "@rendering/vortex-product-loader";
const changed = (x, y) => x !== y;
export function createVortexReactBridge() {
  let busy = false;
  let queuedSyncRequest;
  let queuedSyncRequestRaf;
  let previous;
  let disposed = false;
  return {
    syncWithVortex(_a) {
      return __awaiter(this, arguments, void 0, function (_ref) {
        var _this = this;
        let {
          previewConfig,
          productConfig,
          initializationOptions,
          onError,
          onAfterVortexUpdate,
          onBeforeVortexUpdate,
          preserveCamera,
          preservePreview,
          vortexDocumentAdapter
        } = _ref;
        return function* () {
          var _b, _c, _d, _e, _f, _g, _h;
          queuedSyncRequest = undefined;
          cancelAnimationFrame(queuedSyncRequestRaf);
          if (!previewConfig || !productConfig) {
            return {
              preview: previous === null || previous === void 0 ? void 0 : previous.preview
            }; // loading one of these attributes return current preview
          }
          if (busy) {
            // Vortex can only handle 1 sync at the time, wait for the current one to be finished
            queuedSyncRequest = {
              previewConfig,
              productConfig,
              initializationOptions,
              onError,
              onAfterVortexUpdate,
              onBeforeVortexUpdate,
              preserveCamera,
              preservePreview,
              vortexDocumentAdapter
            };
            return {
              preview: previous === null || previous === void 0 ? void 0 : previous.preview
            };
          }
          const createNewPreview = () => __awaiter(_this, void 0, void 0, function* () {
            var _j, _k, _l, _m;
            // Dispose the old preview
            if (previous) {
              try {
                previous.preview.dispose();
                previous.vortexDocumentAdapter.dispose();
              } catch (e) {
                console.warn("Error in preview.dispose()");
                console.warn(e);
              }
            }
            // Create a new preview and load the product
            try {
              onBeforeVortexUpdate();
              const preview = new Preview(Object.assign({}, previewConfig));
              const product = yield loadProduct(productConfig);
              yield preview.load(product, surfaces => vortexDocumentAdapter.generateCanvases(surfaces), {
                scaleToInitialState: initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.scaleToInitialState
              });
              if (initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.cameraOrientation) {
                preview.adjustRotation({
                  azimuth: initializationOptions.cameraOrientation.azimuthAngle,
                  polar: (_j = initializationOptions.cameraOrientation.polarAngle) !== null && _j !== void 0 ? _j : Math.PI / 2
                });
              }
              if ((_k = initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.swivel) === null || _k === void 0 ? void 0 : _k.enabled) {
                startSwivel(preview, (_l = initializationOptions.swivel.range) !== null && _l !== void 0 ? _l : 0.6, (_m = initializationOptions.swivel.damping) !== null && _m !== void 0 ? _m : 0.55);
              }
              preview.start();
              onAfterVortexUpdate();
              previous = {
                preview,
                previewConfig,
                productConfig,
                initializationOptions,
                vortexDocumentAdapter
              };
            } catch (e) {
              onError(e);
              onAfterVortexUpdate(e);
            }
          });
          busy = true;
          // New previewConfig/productLoadingOptions: create a new preview
          if (changed(previous === null || previous === void 0 ? void 0 : previous.previewConfig, previewConfig) || changed(previous === null || previous === void 0 ? void 0 : previous.initializationOptions, initializationOptions)) {
            yield createNewPreview();
          } else {
            if (!previous) {
              console.log("syncWithVortex: ??");
              return {
                preview: undefined
              };
            }
            // New productConfig: load new product
            if (changed(previous.productConfig, productConfig)) {
              if (preserveCamera || preservePreview) {
                // Load a new product onto existing preview
                try {
                  onBeforeVortexUpdate();
                  const product = yield loadProduct(productConfig);
                  yield previous.preview.load(product, surfaces => vortexDocumentAdapter.generateCanvases(surfaces), {
                    scaleToInitialState: initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.scaleToInitialState
                  });
                  // Adjust camera orientation instantly to cameraOptions.initialOrientation
                  if (!preserveCamera && previewConfig.cameraOptions.initialOrientation) {
                    yield previous.preview.adjustRotation({
                      azimuth: previewConfig.cameraOptions.initialOrientation.azimuthAngle,
                      polar: previewConfig.cameraOptions.initialOrientation.polarAngle,
                      duration: 0
                    });
                  }
                  // Adjust camera orientation and swivel if not preserving camera
                  if (!preserveCamera && (initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.cameraOrientation)) {
                    previous.preview.adjustRotation({
                      azimuth: (_b = initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.cameraOrientation) === null || _b === void 0 ? void 0 : _b.azimuthAngle,
                      polar: (_d = (_c = initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.cameraOrientation) === null || _c === void 0 ? void 0 : _c.polarAngle) !== null && _d !== void 0 ? _d : Math.PI / 2,
                      duration: (_e = initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.cameraOrientation) === null || _e === void 0 ? void 0 : _e.duration
                    });
                  }
                  if (!preserveCamera && ((_f = initializationOptions === null || initializationOptions === void 0 ? void 0 : initializationOptions.swivel) === null || _f === void 0 ? void 0 : _f.enabled)) {
                    startSwivel(previous.preview, (_g = initializationOptions.swivel.range) !== null && _g !== void 0 ? _g : 0.6, (_h = initializationOptions.swivel.damping) !== null && _h !== void 0 ? _h : 0.55);
                  }
                  onAfterVortexUpdate();
                } catch (e) {
                  onError(e);
                  onAfterVortexUpdate(e);
                }
              } else {
                // Create a new preview if preserveCamera is false
                yield createNewPreview();
              }
            } else if (changed(previous.vortexDocumentAdapter, vortexDocumentAdapter)) {
              // New vortexDocumentAdapter: update preview
              try {
                onBeforeVortexUpdate();
                yield previous.preview.update(surfaces => vortexDocumentAdapter.generateCanvases(surfaces));
                onAfterVortexUpdate();
              } catch (e) {
                onError(e);
                onAfterVortexUpdate(e);
              }
            }
            previous = {
              preview: previous.preview,
              previewConfig,
              productConfig,
              initializationOptions,
              vortexDocumentAdapter
            };
          }
          busy = false;
          // Dispose preview while component unmounted in between awaits
          if (disposed && previous) {
            previous.preview.dispose();
            previous.vortexDocumentAdapter.dispose();
          } // Handle a queued request
          else if (queuedSyncRequest) {
            const newRequest = queuedSyncRequest;
            queuedSyncRequest = undefined;
            // cancel previous queued request
            cancelAnimationFrame(queuedSyncRequestRaf);
            queuedSyncRequestRaf = requestAnimationFrame(() => {
              // Can be disposed in between or a new request came in (busy)
              if (!disposed && !busy) {
                _this.syncWithVortex(newRequest);
              }
            });
          }
          return {
            preview: previous === null || previous === void 0 ? void 0 : previous.preview
          };
        }();
      });
    },
    dispose() {
      if (previous) {
        previous.preview.dispose();
        previous.vortexDocumentAdapter.dispose();
        queuedSyncRequest = undefined;
        cancelAnimationFrame(queuedSyncRequestRaf);
      }
      disposed = true;
    }
  };
}